/**
 * COMMANDES & RÉCEPTIONS — le vocabulaire et les briques PARTAGÉES entre la page et ses modales.
 *
 * Tout ici est RELEVÉ de la maquette (écran `isAchats`) : les couleurs d'état (`KC`), les libellés
 * spéciaux (« Ouvert — ligne fantôme », « Intégré — corrigé »), les pastilles, les impressions A4.
 * Une seule table par vocabulaire — la page et les modales consomment, jamais ne redéfinissent.
 */
import { fineFromInput } from './order-editor-grid';
import type { DeliveryNoteDetail, BonResume, CommandeDetaillee, CommandeResume, EtatDeCommande, EtatDuBon, ResumeDuBon, SourceDeCommande, SourceDuBon, StockCorrectionReason } from '../api';
import { fieldLabel } from '../vocabulary';
import { key, t, type Translatable } from '../i18n';

export const MONO = 'var(--font-mono)';

/** Les états d'un bon — couleurs du `KC` maquette (ctl ambre, rdy teal, part bleu, done encre). */
export const ETATS: Record<EtatDuBon, { l: Translatable; bg: string; fg: string }> = {
  to_check: { l: key('À contrôler'), bg: 'var(--amber-bg)', fg: 'var(--amber-fg)' },
  ready: { l: key('Contrôlé — à intégrer'), bg: 'var(--teal-100)', fg: 'var(--teal-800)' },
  partial: { l: key('Partiel'), bg: 'var(--blue-bg)', fg: 'var(--blue-fg)' },
  received: { l: key('Intégré'), bg: 'var(--ink-100)', fg: 'var(--ink-600)' },
  closed: { l: key('Clos — reliquat'), bg: 'var(--ink-100)', fg: 'var(--ink-600)' },
};

/**
 * Le LIBELLÉ d'un bon — trois cas spéciaux relevés de la maquette (`blStat`) :
 * « Ouvert — ligne fantôme » (tout le connu est entré mais une fantôme subsiste), « Intégré — corrigé »
 * (des corrections post-intégration existent), sinon le libellé de l'état.
 * ⚠️ TOUJOURS TRADUIT, dans les trois cas : la fonction rendait deux phrases traduites et, dans le cas courant, la
 *    CLÉ de l'état — que la pastille, la liste et le papier affichaient en français pour tous les comptes.
 */
export function libelleBon(resume: ResumeDuBon, corrected: boolean): string {
  if (resume.etat === 'partial' && resume.unknownLines > 0 && resume.received >= resume.expected && resume.received > 0) {
    return t('Ouvert — ligne fantôme');
  }
  if (resume.etat === 'received' && corrected) return t('Intégré — corrigé');
  return t(ETATS[resume.etat].l);
}

/**
 * LA LIGNE D'IDENTITÉ D'UN BON — fournisseur, réception, document, commande. L'écran et le papier disent la même.
 * ⚠️ DEUX PHRASES ENTIÈRES selon qu'une commande est rattachée : la mention collée à part (« · sans commande ») arrivait
 *    chez le traducteur sans sa phrase.
 */
export function deliveryNoteSubtitle(b: Pick<DeliveryNoteDetail, 'supplierLabel' | 'deliveredOn' | 'createdAt' | 'sourceRef' | 'orderRef'>): string {
  const vars = { v: b.supplierLabel ?? '—', date: dateCourte(b.deliveredOn ?? b.createdAt), doc: b.sourceRef ?? '—' };
  return b.orderRef
    ? t('{v} · reçu le {date} · document {doc} · commande {commande}', { ...vars, commande: b.orderRef })
    : t('{v} · reçu le {date} · document {doc} · sans commande', vars);
}

/** LA LIGNE D'IDENTITÉ D'UNE COMMANDE — fournisseur, note éventuelle, émission ; une phrase entière par cas. */
export function orderSubtitle(c: Pick<CommandeDetaillee, 'supplierLabel' | 'note' | 'orderedOn' | 'createdAt'>): string {
  const vars = { v: c.supplierLabel ?? '—', date: dateCourte(c.orderedOn ?? c.createdAt) };
  return c.note ? t('{v} · {note} · émise le {date}', { ...vars, note: c.note }) : t('{v} · émise le {date}', vars);
}

/** Les états d'une commande — couleurs du `KC` maquette (draft VIOLET, wait ENCRE, part bleu, ok teal, cls AMBRE). */
export const ETATS_CMD: Record<EtatDeCommande, { l: Translatable; bg: string; fg: string }> = {
  draft: { l: key('Brouillon — non envoyée'), bg: 'var(--violet-bg)', fg: 'var(--violet-fg)' },
  waiting: { l: key('En attente de livraison'), bg: 'var(--ink-100)', fg: 'var(--ink-600)' },
  partial: { l: key('Partiellement livrée'), bg: 'var(--blue-bg)', fg: 'var(--blue-fg)' },
  delivered: { l: key('Soldée'), bg: 'var(--teal-100)', fg: 'var(--teal-800)' },
  closed: { l: key('Clôturée — reliquat abandonné'), bg: 'var(--amber-bg)', fg: 'var(--amber-fg)' },
};

/**
 * LA TRANSMISSION D'UNE COMMANDE AU FOURNISSEUR — envoyée (le dernier envoi réel), à transmettre, ou rien à en dire.
 * ⚠️ « À TRANSMETTRE » NE VAUT QUE POUR UNE SAISIE DU BACK-OFFICE EN ATTENTE DE LIVRAISON : une commande EDI ou
 *    importée est partie par un autre canal, et une commande déjà livrée a manifestement été reçue — la dire
 *    « à transmettre » inventerait un oubli (§23.0).
 */
export function orderTransmission(c: Pick<CommandeResume, 'sentAt' | 'source' | 'resume'>): 'sent' | 'toSend' | null {
  if (c.resume.etat === 'draft') return null;
  if (c.sentAt != null) return 'sent';
  return c.source === 'manual' && c.resume.etat === 'waiting' ? 'toSend' : null;
}

/**
 * LE STATUT AFFICHÉ D'UNE COMMANDE — son état, précisé par sa transmission tant qu'elle attend sa livraison
 * (maquette : « Envoyée — en attente de livraison » ; D1 : « Validée — à transmettre »).
 */
export function orderStatusTone(c: Pick<CommandeResume, 'sentAt' | 'source' | 'resume'>): { l: Translatable; bg: string; fg: string } {
  const base = ETATS_CMD[c.resume.etat];
  if (c.resume.etat !== 'waiting') return base;
  const tr = orderTransmission(c);
  return tr === 'sent' ? { ...base, l: key('Envoyée — en attente de livraison') }
    : tr === 'toSend' ? { l: key('Validée — à transmettre'), bg: 'var(--amber-bg)', fg: 'var(--amber-fg)' }
      : base;
}

/** Les sources, relevées (`ASRC`) — la saisie et la reprise complètent (elles existent en vrai). */
export const SOURCES: Record<SourceDuBon, { l: Translatable; bg: string; fg: string }> = {
  // ⚠️ `key()` AUSSI pour un sigle : la pastille résout CHAQUE libellé, et une table mi-clés mi-texte brut obligerait
  //    le rendu à savoir lesquels traduire.
  edi: { l: key('EDI'), bg: 'var(--blue-bg)', fg: 'var(--blue-fg)' },
  import: { l: key('IMPORT'), bg: 'var(--violet-bg)', fg: 'var(--violet-fg)' },
  scan: { l: key('SCAN DIRECT'), bg: 'var(--teal-100)', fg: 'var(--teal-800)' },
  'mobile-partner': { l: key('MOBILE PARTNER'), bg: 'var(--teal-100)', fg: 'var(--teal-800)' },
  manual: { l: key('SAISIE'), bg: 'var(--ink-100)', fg: 'var(--ink-600)' },
  // Repris du logiciel précédent à la bascule — ton neutre : ce n'est ni un flux, ni une saisie d'ici.
  legacy: { l: key('REPRISE'), bg: 'var(--ink-100)', fg: 'var(--ink-600)' },
};

/** Motifs de correction post-intégration — le référentiel FERMÉ, mêmes libellés que l'écran Stock. */
export const MOTIFS_CORRECTION: { v: StockCorrectionReason; l: Translatable }[] = [
  { v: 'inventory', l: key('Inventaire') },
  { v: 'breakage', l: key('Casse') },
  { v: 'theft', l: key('Vol / démarque') },
  { v: 'manual_correction', l: key('Correction manuelle') },
];

/**
 * L'ORIGINE d'un prix — badge + explication (relevés, complétés de l'échelon `order` réel D5).
 * Un prix dont on ignore l'origine est un prix qu'on finit par ne plus croire.
 */
export function origineDuPrix(priceSource: string | null): { ps: string; psT: string } {
  switch (priceSource) {
    case 'order': return { ps: t('CMDE'), psT: t('Prix de la commande rattachée (ORDRSP) — échelon 1 de l’échelle de valorisation') };
    case 'bl': return { ps: t('PRIX BL'), psT: t('Prix porté par le document (EDI ou saisie du bon)') };
    case 'fiche': return { ps: t('FICHE'), psT: t('Repli : PA catalogue de la fiche article (jamais le PMP)') };
    case 'manual': return { ps: t('MANUEL'), psT: t('Prix corrigé à la main au contrôle') };
    default: return { ps: '—', psT: t('Aucun prix — à valoriser') };
  }
}

/**
 * Date courte « jj/mm » — et « jj/mm/aa » HORS année courante : avec la reprise, un bon de 2022
 * coexiste avec ceux d'aujourd'hui, et « 14/08 » seul les rendrait indiscernables (audit 2026-08-28).
 */
export function dateCourte(ms: number | null): string {
  if (ms == null) return '—';
  const d = new Date(ms);
  const jjmm = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
  return d.getFullYear() === new Date().getFullYear() ? jjmm : `${jjmm}/${String(d.getFullYear()).slice(2)}`;
}

/** L'heure « hh:mm » d'un instant — ce qui distingue deux envois du même jour. */
export function heureCourte(ms: number): string {
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export const dateIso = (ms: number): string => {
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** Le prix à l'échelle fine, rendu « 28,40 » ; `null` = « — », jamais 0,00 (absent n'est pas gratuit). */
export const euroFin = (fine: number | null): string =>
  (fine == null ? '—' : (fine / 10000).toFixed(2).replace('.', ','));

/**
 * « 28,40 » saisi → échelle fine ; vide → null (à valoriser) ; illisible → undefined (on n'écrit rien).
 * ⚠️ UNE SEULE LECTURE DU PRIX D'ACHAT pour le BL et la commande (`fineFromInput`) : quatre décimales au
 *    plus, REFUSÉES au-delà — une lecture par flottant arrondissait « 28,40567 » en silence.
 */
export const finDepuisSaisie = fineFromInput;

// ── Les pastilles ─────────────────────────────────────────────────────────────────────────────────
/**
 * La pastille d'état. `l` est un libellé DÉJÀ TRADUIT (celui de `libelleBon`) ; sans lui, la pastille résout la CLÉ de
 * son ton — `tone.l` vient d'`ETATS`/`ETATS_CMD`, déclarée par `key()`, et rendue nue elle s'affichait en français.
 */
export function StatusPill({ tone, l }: { tone: { l: Translatable; bg: string; fg: string }; l?: string }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', height: 24, padding: '0 10px', borderRadius: 'var(--r-pill)',
      background: tone.bg, color: tone.fg, fontSize: '11.5px', fontWeight: 700, whiteSpace: 'nowrap', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis',
    }}>{l ?? t(tone.l)}</span>
  );
}

export function Source({ s }: { s: SourceDuBon }) {
  const source = SOURCES[s] ?? SOURCES.manual;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', height: 20, padding: '0 7px', borderRadius: 'var(--r-xs)',
      background: source.bg, color: source.fg, fontSize: '9.5px', fontWeight: 800, letterSpacing: '.04em', whiteSpace: 'nowrap',
    }}>{t(source.l)}</span>
  );
}

/** La pastille de source d'une commande — mêmes tons que les bons, `legacy` compris. */
export function SourceCmd({ s }: { s: SourceDeCommande }) {
  const source = s === 'edi' ? SOURCES.edi : s === 'import' ? SOURCES.import : s === 'legacy' ? SOURCES.legacy : SOURCES.manual;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', height: 20, padding: '0 7px', borderRadius: 'var(--r-xs)',
      background: source.bg, color: source.fg, fontSize: '9.5px', fontWeight: 800, letterSpacing: '.04em', whiteSpace: 'nowrap',
    }}>{t(source.l)}</span>
  );
}

/** Le badge d'écart — ambre : il dit « écart », pas « erreur » (un écart est une information). */
export function Ecart({ n }: { n: number }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', height: 19, padding: '0 6px', borderRadius: 'var(--r-xs)',
      background: 'var(--amber-bg)', color: 'var(--amber-fg)',
      fontSize: '10.5px', fontWeight: 800, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums',
    }}>{n > 0 ? `+${n}` : `−${Math.abs(n)}`}</span>
  );
}

// ── L'impression A4 — gabarits RELEVÉS de la maquette (`blPrintA4` / `cmdPrintA4`) ────────────────
const A4_CSS = '@page{size:A4;margin:16mm}body{font:12px/1.5 system-ui,sans-serif;color:#131618;margin:0}'
  + 'h1{font-size:19px;margin:0}.sub{color:#6c747c;margin:3px 0 14px}.badge{display:inline-block;border:1px solid #c4ccd2;border-radius:999px;padding:2px 9px;font-size:10.5px;font-weight:700;margin-left:8px;vertical-align:2px}'
  + 'table{width:100%;border-collapse:collapse;margin-top:6px}th{font-size:9.5px;text-transform:uppercase;letter-spacing:.05em;color:#6c747c;text-align:left;border-bottom:1.5px solid #131618;padding:5px 7px}'
  + 'td{border-bottom:1px solid #e2e6ea;padding:6px 7px;vertical-align:top}.m{font-family:ui-monospace,monospace;font-size:10.5px}.r{text-align:right;font-variant-numeric:tabular-nums}.c{text-align:center}th.r{text-align:right}th.c{text-align:center}'
  + '.e{font-weight:700}.tot td{border-bottom:none;border-top:1.5px solid #131618;font-weight:700}'
  + '.warn{margin-top:10px;padding:8px 10px;border:1px solid #c4ccd2;border-radius:6px;background:#f7f8f9;font-size:11px}'
  + '.foot{margin-top:18px;color:#97a0a8;font-size:10px}';

const esc = (v: unknown) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;');
const editionDuJour = () => `${dateCourte(Date.now())}/${new Date().getFullYear()}`;

/** Une cellule d'en-tête : son mot et son alignement (les classes `c` / `r` de `A4_CSS`). */
export interface PrintHeaderCell { label: string; align: 'left' | 'center' | 'right' }

/**
 * LES CELLULES D'EN-TÊTE du BL imprimé en A4 — les colonnes de la maquette mot pour mot, SAUF la taille,
 * qui porte le mot du magasin : le papier dit ce que l'écran du même bon dit.
 * ⚠️ APPELÉE AU CLIC, JAMAIS AU CHARGEMENT : `fieldLabel` lu dans une constante de module figerait
 *    « Taille » avant que le contexte magasin n'arrive. C'est aussi ce qui rend la fonction PURE et
 *    éprouvable sans DOM — l'impression (iframe, `print()`), elle, ne l'est pas.
 * ⚠️ « Article », « Code », « PA retenu » sont des mots de DOCUMENT, pas des champs du catalogue :
 *    l'écran les garde en dur, le papier aussi.
 */
export function printHeaderCells(): PrintHeaderCell[] {
  return [
    { label: t('Article'), align: 'left' }, { label: t('Code'), align: 'left' }, { label: fieldLabel('size'), align: 'center' },
    { label: t('Attendu'), align: 'right' }, { label: t('Contrôlé'), align: 'right' }, { label: t('Écart'), align: 'right' },
    { label: t('Entré'), align: 'right' }, { label: t('PA retenu'), align: 'right' },
  ];
}

/** La ligne `<tr>` d'en-tête, mots échappés — un mot de magasin est un littéral libre (« A & B »). */
export function printHeaderRow(): string {
  const cls = { left: '', center: ' class="c"', right: ' class="r"' } as const;
  return `<tr>${printHeaderCells().map((c) => `<th${cls[c.align]}>${esc(c.label)}</th>`).join('')}</tr>`;
}

/** Ouvre le document dans un iframe caché et lance l'impression — le patron de la maquette. */
function imprimer(html: string): void {
  const f = document.createElement('iframe');
  f.style.cssText = 'position:fixed;right:100%;bottom:100%;width:0;height:0;border:0';
  document.body.appendChild(f);
  f.onload = () => {
    try { f.contentWindow!.focus(); f.contentWindow!.print(); } catch { /* ⚠️ un blocage d'impression navigateur n'a pas de rattrapage ici — l'aperçu est déjà à l'écran */ }
    window.setTimeout(() => f.remove(), 4000);
  };
  f.srcdoc = html;
}

/** « Imprimer la liste du BL (A4) » — colonnes et textes mot pour mot (maquette `blPrintA4`). */
export function imprimerBonA4(b: DeliveryNoteDetail, libelle: string): void {
  let att = 0; let ctl = 0; let int = 0;
  const rows = b.lines.filter((l) => l.known).map((l) => {
    const ec = l.checkedQty == null ? null : l.checkedQty - l.expectedQty;
    att += l.expectedQty; ctl += l.checkedQty ?? 0; int += l.receivedQty;
    return `<tr><td>${esc(l.name ?? l.gencode)}${l.color ? ` · ${esc(l.color)}` : ''}</td><td class="m">${esc(l.gencode)}</td><td class="m c">${esc(l.size ?? '—')}</td>`
      + `<td class="r">${l.expectedQty}</td><td class="r">${l.checkedQty ?? '—'}</td><td class="r${ec ? ' e' : ''}">${ec == null ? '—' : ec === 0 ? '0' : ec > 0 ? `+${ec}` : ec}</td>`
      + `<td class="r">${l.receivedQty}</td><td class="r m">${esc(euroFin(l.unitPriceFine))}</td></tr>`;
  }).join('');
  const unk = b.lines.filter((l) => !l.known);
  // ⚠️ LE PAPIER SE TRADUIT COMME L'ÉCRAN, et c'est la phrase TRADUITE qu'on échappe — valeurs comprises : une
  //    traduction peut porter un « & », un fournisseur aussi. La clé ne porte jamais de balisage (une clé balisée
  //    emporterait la page au catalogue), la balise reste dans le gabarit.
  const unkRows = unk.length
    ? `<p class="warn">${esc(t('{n} ligne fantôme exclue de l’intégration : {v}', { n: unk.length, v: unk.map((u) => `${u.gencode} × ${u.checkedQty ?? 0}`).join(' · ') }))}</p>` : '';
  imprimer(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${esc(b.number)}</title><style>${A4_CSS}</style></head><body>`
    + `<h1>${esc(t('Bon de livraison {v}', { v: b.number }))}<span class="badge">${esc(libelle)}</span></h1>`
    + `<p class="sub">${esc(deliveryNoteSubtitle(b))}</p>`
    + `<table><thead>${printHeaderRow()}</thead>`
    + `<tbody>${rows}<tr class="tot"><td colspan="3">${esc(t('Totaux'))}</td><td class="r">${att}</td><td class="r">${ctl}</td><td class="r"></td><td class="r">${int}</td><td></td></tr></tbody></table>`
    + unkRows
    + `<p class="foot">${esc(t('Sliderz Back-office — édition du {v}. Une ligne intégrée ne se modifie plus : toute correction passe par un mouvement de stock tracé.', { v: editionDuJour() }))}</p>`
    + '</body></html>');
}

// ── Les petits boutons d'action des lignes de liste (30 × 30, relevés) ────────────────────────────
export function BtnIcone({ title, ariaLabel, disabled, tone = 'neutre', onClick, children }: {
  title: string; ariaLabel: string; disabled?: boolean; tone?: 'neutre' | 'rose'; onClick: () => void; children: React.ReactNode;
}) {
  const off = disabled === true;
  const rose = tone === 'rose';
  return (
    <button
      type="button" aria-label={ariaLabel} aria-disabled={off || undefined} title={title}
      onClick={(e) => { e.stopPropagation(); if (!off) onClick(); }}
      style={{
        width: 30, height: 30, border: `1px solid ${off ? 'var(--ink-200)' : rose ? 'var(--rose)' : 'var(--ink-200)'}`,
        borderRadius: 'var(--r-xs)', background: 'var(--paper)',
        color: off ? 'var(--ink-300)' : rose ? 'var(--rose-fg)' : 'var(--ink-600)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, flexShrink: 0,
        opacity: off ? 0.45 : 1, cursor: off ? 'not-allowed' : 'pointer',
      }}
    >{children}</button>
  );
}

/** L'icône étiquettes (code-barres court) de la maquette — trois emplois, un seul dessin. */
export function IconeEtiquette({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" aria-hidden>
      <path d="M3 6v12M7 6v12M10 6v8M13 6v12M16.5 6v8M20 6v12M10 17.5v.5M16.5 17.5v.5" />
    </svg>
  );
}

/** L'icône imprimante de la maquette. */
export function IconeImprimante({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M6 9V3h12v6M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2M6 14h12v7H6z" />
    </svg>
  );
}

/** Ce qu'un bon peut étiqueter — les trois sources de quantité de la modale (maquette peSrcRows). */
export interface LigneAEtiqueter {
  gencode: string;
  /** Pièces déjà entrées en stock. */
  done: number;
  /** Contrôlées, pas encore entrées. */
  ctl: number;
  /** Attendues, non contrôlées. */
  wait: number;
}

export function lignesAEtiqueterDuBon(b: BonResume & { lines?: DeliveryNoteDetail['lines'] }): LigneAEtiqueter[] {
  return (b.lines ?? []).filter((l) => l.known).map((l) => ({
    gencode: l.gencode,
    done: l.receivedQty,
    ctl: l.checkedQty == null ? 0 : Math.max(0, l.checkedQty - l.receivedQty),
    wait: l.checkedQty == null ? Math.max(0, l.expectedQty - l.receivedQty) : 0,
  }));
}
